import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';
import Loader from '../../../common/Loader';
import { useNotificationContext } from '../../layout/FloatingNotification';
import Modal from '../../../common/Modal';
import { Button } from '../../../common/Button';
import hooverNoIcon from '../../../../../assets/icons/account-termination/hoover-no.svg';
import styles from './UnlinkDeviceModal.scss';

interface UnlinkDeviceDialogProps {
  onUnlink: () => Promise<void>;
  onCancel: () => void;
  visible: boolean;
  isRouter?: boolean;
  error?: string;
}

const UnlinkDeviceDialog = ({ onUnlink, onCancel, error, visible, isRouter }: UnlinkDeviceDialogProps) => {
  const { addNotification } = useNotificationContext();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(false);
  }, [error]);

  const onUnlinkClick = () => {
    setIsLoading(true);
    onUnlink()
      .then(
        () => {
          addNotification('deviceDeleted');
        },
        () => {
          addNotification('deviceDeletionError');
        },
      )
      .finally(() => {
        setIsLoading(false);
        onCancel();
      });
  };

  const onClose = () => {
    setIsLoading(false);
    onCancel();
  };

  const confirmButtonLabel = isRouter ? (
    <FormattedMessage
      id="profile.deviceManagement.unlinkModal.router.confirmButton"
      defaultMessage="Delete device"
      description="Button that unlinks device and the account"
    />
  ) : (
    <FormattedMessage
      id="profile.deviceManagement.unlinkModal.confirmButton"
      defaultMessage="Unlink device"
      description="Button that unlinks device and the account"
    />
  );

  return (
    <Modal isOpen={visible} className={clsx(styles.modal, error && styles.modalWithError)} onClose={onClose}>
      <img src={hooverNoIcon} className={styles.hooverIcon} width="196" height="147" alt="" />

      <h3 className={styles.title}>
        {isRouter ? (
          <FormattedMessage
            id="profile.deviceManagement.unlinkModal.router.title"
            defaultMessage="Do you want to delete device?"
            description="Title of the modal to remove a device from the account"
          />
        ) : (
          <FormattedMessage
            id="profile.deviceManagement.unlinkModal.title"
            defaultMessage="Do you want to unlink your device?"
            description="Title of the modal to remove a device from the account"
          />
        )}
      </h3>

      <p className={styles.text}>
        {isRouter ? (
          <FormattedMessage
            id="profile.deviceManagement.unlinkModal.router.text"
            defaultMessage="Deleting this device means you won’t be able to use VPN on your router. It might also cause temporary internet issues when the VPN disconnects"
          />
        ) : (
          <FormattedMessage
            id="profile.deviceManagement.unlinkModal.text"
            defaultMessage="Your device will be unlinked from the account and you will be logged out of the app. You can link it again later."
          />
        )}
      </p>

      <div className={styles.buttonsLine}>
        <Button className={styles.cancelButton} onClick={onClose}>
          <FormattedMessage
            id="profile.deviceManagement.unlinkModal.cancelButton"
            defaultMessage="Cancel"
            description="Button that closes unlink device modal"
          />
        </Button>

        <Button className={styles.unlinkButton} onClick={onUnlinkClick}>
          {isLoading ? <Loader size={20} /> : confirmButtonLabel}
        </Button>
      </div>

      {error ? <span className={styles.error}>{error}</span> : null}
    </Modal>
  );
};

export default UnlinkDeviceDialog;
